import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { istToday } from "@/lib/istDate";
import { useAuth } from "@/contexts/AuthContext";

const TYPES = ["Marketing", "Consumer Discount"];
// NEXT BUILD PASS 1B (item 21b): the stored value stays "Consumer Discount", the screen says this
const TYPE_LABEL = { "Marketing": "Marketing", "Consumer Discount": "Promo / consumer discount" };
const SETTLEMENTS = ["Paid upfront", "Adjusted against orders"];
const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PlatformInvoicesPage() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).some(r => ["Admin", "admin"].includes(r)) || (user?.permissions || []).includes("admin.full_access");
  const [rows, setRows] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fChannel, setFChannel] = useState("");
  const [fMonth, setFMonth] = useState("");
  const [fType, setFType] = useState("");
  const [fSettlement, setFSettlement] = useState("");
  const blank = {
    channel_id: "", channel_name: "", deduction_type: "Marketing", period_month: istToday().slice(0, 7),
    invoice_number: "", invoice_date: istToday(), amount_excl_gst: "", gst_amount: "", amount: "",
    settlement: "Paid upfront", linked_order_numbers: "", notes: "",
    allocations: [{ order_number: "", amount: "" }],
  };
  const [form, setForm] = useState(blank);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fChannel) params.channel_id = fChannel;
      if (fMonth) params.month = fMonth;
      if (fType) params.type = fType;
      const [d, ch] = await Promise.all([
        api.get("/platform-deductions", { params }),
        api.get("/channels"),
      ]);
      setRows(d.data || []);
      setChannels(ch.data || []);
    } catch (e) { toast.error("Could not load the platform invoices"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fChannel, fMonth, fType]);

  const save = async () => {
    if (!form.channel_id) return toast.error("Choose the platform");
    const total = Number(form.amount || 0) || (Number(form.amount_excl_gst || 0) + Number(form.gst_amount || 0));
    if (!(total > 0)) return toast.error("Enter the invoice amount");
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: total,
        amount_excl_gst: Number(form.amount_excl_gst || 0),
        gst_amount: Number(form.gst_amount || 0),
        channel_name: (channels.find(c => c._id === form.channel_id) || {}).name || "",
        linked_order_numbers: String(form.linked_order_numbers || "").split(",").map(s => s.trim()).filter(Boolean),
        // item 22a — the allocation table, only when the platform cut it from our orders
        allocations: form.settlement === "Adjusted against orders"
          ? (form.allocations || []).filter(a => String(a.order_number || "").trim() && Number(a.amount || 0) > 0)
              .map(a => ({ order_number: String(a.order_number).trim(), amount: Number(a.amount) }))
          : [],
      };
      if (editing) await api.put(`/platform-deductions/${editing}`, payload);
      else await api.post("/platform-deductions", payload);
      toast.success(editing ? "Invoice updated" : "Invoice added");
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the invoice ${r.invoice_number || r.reference_number || ""}? The P&L for ${r.period_month} will change.`)) return;
    try {
      await api.delete(`/platform-deductions/${r._id}`);
      toast.success("Invoice deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const shown = rows.filter(r => !fSettlement || (r.settlement || "Paid upfront") === fSettlement);
  const total = shown.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalImpact = shown.reduce((s, r) => s + Number(r.profit_impact || 0), 0);

  return (
    <div className="p-6" data-testid="platform-invoices-page">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-950">Platform Invoices</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Marketing and consumer-discount invoices from the platforms. Each one is counted once in its month in the P&amp;L, whichever way it is settled.</p>
        </div>
        <Button className="rounded-sm h-8 text-xs" data-testid="add-platform-invoice" onClick={() => { setEditing(null); setForm(blank); setOpen(true); }}>
          <Plus size={14} className="mr-1" /> Add invoice
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <select className="h-8 text-xs border border-zinc-200 rounded-sm px-2" value={fChannel} onChange={e => setFChannel(e.target.value)} data-testid="filter-platform">
          <option value="">All platforms</option>
          {channels.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        <Input type="month" value={fMonth} onChange={e => setFMonth(e.target.value)} className="h-8 text-xs rounded-sm w-36" data-testid="filter-month" />
        <select className="h-8 text-xs border border-zinc-200 rounded-sm px-2" value={fType} onChange={e => setFType(e.target.value)} data-testid="filter-type">
          <option value="">All types</option>
          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="h-8 text-xs border border-zinc-200 rounded-sm px-2" value={fSettlement} onChange={e => setFSettlement(e.target.value)} data-testid="filter-settlement">
          <option value="">All settlements</option>
          {SETTLEMENTS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <span className="text-xs text-zinc-500 self-center ml-2" data-testid="platform-invoices-total">{shown.length} invoices · {fmt(total)} · profit impact {fmt(totalImpact)}</span>
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-left">
            <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-semibold [&>th]:text-zinc-600">
              <th>Invoice no.</th><th>Date</th><th>Platform</th><th>Type</th><th>Month</th>
              <th className="text-right">Excl. GST</th><th className="text-right">GST</th>
              <th className="text-right">Total</th><th className="text-right">Profit impact ₹</th>
              <th>Settlement</th><th>Adjusted against</th><th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={12} className="text-center py-8 text-zinc-400">Loading…</td></tr>
              : shown.length === 0 ? <tr><td colSpan={12} className="text-center py-8 text-zinc-400">No platform invoices yet</td></tr>
              : shown.map(r => (
                <tr key={r._id} className="border-t border-zinc-100 [&>td]:px-3 [&>td]:py-2" data-testid={`platform-invoice-${r._id}`}>
                  <td className="font-mono">{r.invoice_number || r.reference_number || "—"}</td>
                  <td>{r.invoice_date || "—"}</td>
                  <td>{r.channel_name}</td>
                  <td>{TYPE_LABEL[r.deduction_type] || r.deduction_type}</td>
                  <td className="font-mono">{r.period_month}</td>
                  <td className="text-right font-mono">{fmt(r.amount_excl_gst)}</td>
                  <td className="text-right font-mono">{fmt(r.gst_amount)}</td>
                  <td className="text-right font-mono font-semibold">{fmt(r.amount)}</td>
                  <td className="text-right font-mono text-orange-700" data-testid={`pi-profit-impact-${r._id}`}>{fmt(r.profit_impact)}</td>
                  <td><span className="px-1.5 py-0.5 text-[10px] bg-zinc-100 text-zinc-700 rounded-sm">{r.settlement || "Paid upfront"}</span></td>
                  <td className="text-[10px] text-zinc-500" data-testid={`pi-allocations-${r._id}`}>
                    {(r.allocations || []).length
                      ? (r.allocations || []).map(a => `${a.order_number} ${fmt(a.amount)}`).join(", ")
                      : ((r.linked_order_numbers || []).join(", ") || "—")}
                  </td>
                  <td>
                    <button className="p-1 hover:bg-zinc-100 rounded-sm" data-testid={`edit-platform-invoice-${r._id}`}
                            onClick={() => {
                              setEditing(r._id);
                              setForm({ ...blank, ...r, invoice_number: r.invoice_number || r.reference_number || "",
                                        linked_order_numbers: (r.linked_order_numbers || []).join(", "),
                                        allocations: (r.allocations || []).length
                                          ? (r.allocations || []).map(a => ({ order_number: a.order_number, amount: a.amount }))
                                          : [{ order_number: "", amount: "" }] });
                              setOpen(true);
                            }}>
                      <Pencil size={13} className="text-zinc-500" />
                    </button>
                    {isAdmin && (
                      <button className="p-1 hover:bg-red-50 rounded-sm ml-1" data-testid={`delete-platform-invoice-${r._id}`}
                              onClick={() => remove(r)}>
                        <Trash2 size={13} className="text-red-500" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="platform-invoice-dialog">
          <DialogHeader><DialogTitle className="text-base">{editing ? "Edit platform invoice" : "Add platform invoice"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Platform *</Label>
              <select className="mt-1 w-full h-9 text-xs border border-zinc-200 rounded-sm px-2" value={form.channel_id}
                      onChange={e => setForm({ ...form, channel_id: e.target.value })} data-testid="pi-channel">
                <option value="">Choose…</option>
                {channels.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select></div>
            <div><Label className="text-xs">Type *</Label>
              <select className="mt-1 w-full h-9 text-xs border border-zinc-200 rounded-sm px-2" value={form.deduction_type}
                      onChange={e => setForm({ ...form, deduction_type: e.target.value })} data-testid="pi-type">
                {TYPES.map(t => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
              </select></div>
            <div><Label className="text-xs">Invoice no.</Label>
              <Input className="mt-1 h-9 text-xs rounded-sm" value={form.invoice_number}
                     onChange={e => setForm({ ...form, invoice_number: e.target.value })} data-testid="pi-invoice-number" /></div>
            <div><Label className="text-xs">Invoice date</Label>
              <Input type="date" className="mt-1 h-9 text-xs rounded-sm" value={form.invoice_date}
                     onChange={e => setForm({ ...form, invoice_date: e.target.value })} data-testid="pi-invoice-date" /></div>
            <div><Label className="text-xs">Month it belongs to *</Label>
              <Input type="month" className="mt-1 h-9 text-xs rounded-sm" value={form.period_month}
                     onChange={e => setForm({ ...form, period_month: e.target.value })} data-testid="pi-month" /></div>
            <div><Label className="text-xs">Settlement</Label>
              <select className="mt-1 w-full h-9 text-xs border border-zinc-200 rounded-sm px-2" value={form.settlement}
                      onChange={e => setForm({ ...form, settlement: e.target.value })} data-testid="pi-settlement">
                {SETTLEMENTS.map(t => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><Label className="text-xs">Amount excl. GST</Label>
              <Input type="number" step="0.01" className="mt-1 h-9 text-xs rounded-sm" value={form.amount_excl_gst}
                     onChange={e => setForm({ ...form, amount_excl_gst: e.target.value,
                       amount: String(Number(e.target.value || 0) + Number(form.gst_amount || 0)) })}
                     data-testid="pi-excl-gst" /></div>
            <div><Label className="text-xs">GST</Label>
              <Input type="number" step="0.01" className="mt-1 h-9 text-xs rounded-sm" value={form.gst_amount}
                     onChange={e => setForm({ ...form, gst_amount: e.target.value,
                       amount: String(Number(form.amount_excl_gst || 0) + Number(e.target.value || 0)) })}
                     data-testid="pi-gst" /></div>
            <div><Label className="text-xs">Total (leave blank to add the two above)</Label>
              <Input type="number" step="0.01" className="mt-1 h-9 text-xs rounded-sm" value={form.amount}
                     onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="pi-total" /></div>
            <div><Label className="text-xs">Linked orders (optional, comma separated)</Label>
              <Input className="mt-1 h-9 text-xs rounded-sm" value={form.linked_order_numbers}
                     onChange={e => setForm({ ...form, linked_order_numbers: e.target.value })} data-testid="pi-linked-orders" /></div>
            {/* item 21: what this invoice takes off the profit */}
            <div className="col-span-2 border border-orange-200 bg-orange-50/60 rounded-sm px-3 py-2" data-testid="pi-profit-impact">
              <span className="text-[11px] text-orange-800">
                Profit impact {fmt(form.deduction_type === "Marketing"
                  ? Number(form.amount_excl_gst || 0)
                  : (Number(form.amount || 0) || Number(form.amount_excl_gst || 0) + Number(form.gst_amount || 0)))}
                {form.deduction_type === "Marketing"
                  ? " — the GST is claimed back, so profit falls by the amount without GST."
                  : " — no tax invoice, so profit falls by the full amount and GST must be 0."}
              </span>
            </div>
            {/* item 22a: the allocation table */}
            {form.settlement === "Adjusted against orders" && (
              <div className="col-span-2 border border-zinc-200 rounded-sm p-2" data-testid="pi-allocations">
                <Label className="text-xs">Adjusted against orders — the amounts must add up to the invoice total</Label>
                <table className="w-full text-xs mt-2">
                  <thead><tr className="text-left [&>th]:py-1 [&>th]:font-semibold [&>th]:text-zinc-600"><th>Order no.</th><th className="w-40">Amount ₹ (incl GST)</th><th className="w-8"></th></tr></thead>
                  <tbody>
                    {(form.allocations || []).map((a, i) => (
                      <tr key={i}>
                        <td className="pr-2 py-0.5">
                          <Input className="h-8 text-xs rounded-sm" value={a.order_number} placeholder="ORD-00123"
                                 onChange={e => { const al = [...form.allocations]; al[i] = { ...al[i], order_number: e.target.value }; setForm({ ...form, allocations: al }); }}
                                 data-testid={`pi-alloc-order-${i}`} />
                        </td>
                        <td className="pr-2 py-0.5">
                          <Input type="number" step="0.01" className="h-8 text-xs rounded-sm" value={a.amount}
                                 onChange={e => { const al = [...form.allocations]; al[i] = { ...al[i], amount: e.target.value }; setForm({ ...form, allocations: al }); }}
                                 data-testid={`pi-alloc-amount-${i}`} />
                        </td>
                        <td>
                          <button className="text-zinc-400 hover:text-red-600 text-xs px-1" data-testid={`pi-alloc-remove-${i}`}
                                  onClick={() => setForm({ ...form, allocations: form.allocations.filter((_, j) => j !== i) })}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between mt-2">
                  <Button variant="outline" className="h-7 rounded-sm text-[11px]" data-testid="pi-alloc-add"
                          onClick={() => setForm({ ...form, allocations: [...(form.allocations || []), { order_number: "", amount: "" }] })}>
                    Add an order
                  </Button>
                  <span className="text-[11px] text-zinc-500" data-testid="pi-alloc-total">
                    Allocated {fmt((form.allocations || []).reduce((s2, x) => s2 + Number(x.amount || 0), 0))} of {fmt(Number(form.amount || 0))}
                  </span>
                </div>
              </div>
            )}
            <div className="col-span-2"><Label className="text-xs">Notes</Label>
              <Input className="mt-1 h-9 text-xs rounded-sm" value={form.notes}
                     onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="pi-notes" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" className="rounded-sm h-8 text-xs" onClick={() => setOpen(false)} data-testid="pi-cancel">Cancel</Button>
            <Button className="rounded-sm h-8 text-xs" onClick={save} disabled={saving} data-testid="pi-save">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
